import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TralenexLogo } from "@/components/brand/TralenexLogo";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { getPersonasForTenant } from "@/lib/mockData";
import { USE_MOCK_DATA } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * S01 — Sign-In
 *
 * Two flavours selected via REACT_APP_USE_MOCK_DATA:
 *  - Mock (Phase 1): email → persona picker → /auth/redirect bridge.
 *  - API  (Phase 3): email + password → POST /api/auth/login (HttpOnly
 *    cookie) → /dashboard. Persona is bound to the seeded user, so the
 *    picker is skipped.
 */
export default function SignIn() {
  const { signIn } = useAuth();
  const { tenant } = useTenantData();
  const personas = getPersonasForTenant(tenant?.id);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState("email"); // email | persona  (mock only)

  const validateEmail = () => {
    if (!email.trim()) {
      setEmailError("Enter your work email to continue.");
      return false;
    }
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!ok) {
      setEmailError("Enter a valid work email address.");
      return false;
    }
    setEmailError("");
    return true;
  };

  // Mock path: email-only, advance to persona step.
  const handleContinueMock = (e) => {
    e?.preventDefault();
    if (!validateEmail()) return;
    setStage("persona");
  };

  // API path: email + password → /api/auth/login.
  const handleApiSignIn = async (e) => {
    e?.preventDefault();
    setServerError("");
    if (!validateEmail()) return;
    if (!password) {
      setPasswordError("Enter your password to continue.");
      return;
    }
    setPasswordError("");
    setSubmitting(true);
    const result = await signIn(email.trim(), password);
    setSubmitting(false);
    if (result?.ok) {
      navigate(from, { replace: true });
    } else {
      setServerError(result?.message || "Sign-in failed. Try again.");
    }
  };

  const handleSignInMockPersona = (personaId) => {
    navigate("/auth/redirect", {
      state: { personaId, target: from },
      replace: true,
    });
  };

  return (
    <div
      className="relative min-h-screen w-full bg-white flex flex-col"
      data-testid="sign-in-page"
    >
      {/* Sovereign-blue left rail — quiet brand anchor */}
      <div
        aria-hidden="true"
        className="hidden md:block absolute left-0 top-0 bottom-0 w-1.5"
        style={{ background: "#137DC5" }}
      />

      {/* Top bar with TRALENEX logo only */}
      <header className="px-10 lg:px-14 pt-8">
        <TralenexLogo height={72} data-testid="sign-in-tralenex-logo" />
      </header>

      {/* Centre stage */}
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-[420px] anim-fade-up" data-testid="sign-in-content">
          {USE_MOCK_DATA ? (
            stage === "email" ? (
              <>
                {/* Product hero */}
                <div className="mb-12">
                  <h1
                    className="text-display-xl text-ink-primary tracking-tight"
                    style={{ fontFeatureSettings: '"ss01"' }}
                    data-testid="sign-in-product-name"
                  >
                    ALGOS
                  </h1>
                </div>

                {/* Sign-in form (mock SSO) */}
                <form onSubmit={handleContinueMock} noValidate>
                  <label
                    htmlFor="email"
                    className="block text-meta font-semibold text-ink-primary mb-2"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? "email-error" : undefined}
                    data-testid="sign-in-email-input"
                    className={cn(
                      "w-full h-11 rounded-md border bg-white px-3.5 text-[15px] outline-none transition-colors",
                      "border-border-strong focus:border-tralenex-600 focus:ring-2 focus:ring-tralenex-200",
                      emailError && "border-risk-high focus:border-risk-high focus:ring-risk-high/20",
                    )}
                    placeholder="name@company.com"
                  />
                  {emailError && (
                    <p
                      id="email-error"
                      className="mt-2 text-meta text-risk-high"
                      data-testid="sign-in-email-error"
                    >
                      {emailError}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="sign-in-continue-button"
                    className={cn(
                      "mt-4 w-full h-11 inline-flex items-center justify-center gap-2",
                      "rounded-md bg-tralenex-600 text-white text-[15px] font-semibold",
                      "hover:bg-tralenex-700 active:bg-tralenex-900 transition-colors",
                    )}
                  >
                    Continue with single sign-on
                    <ArrowRight size={16} />
                  </button>
                </form>
              </>
            ) : (
              <PersonaStep
                email={email}
                onSelect={handleSignInMockPersona}
                onBack={() => setStage("email")}
                personas={personas}
                tenantName={tenant?.name}
              />
            )
          ) : (
            <>
              {/* Product hero */}
              <div className="mb-12">
                <h1
                  className="text-display-xl text-ink-primary tracking-tight"
                  style={{ fontFeatureSettings: '"ss01"' }}
                  data-testid="sign-in-product-name"
                >
                  ALGOS
                </h1>
              </div>

              {/* Sign-in form (API · email + password) */}
              <form onSubmit={handleApiSignIn} noValidate data-testid="sign-in-api-form">
                <label
                  htmlFor="email"
                  className="block text-meta font-semibold text-ink-primary mb-2"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? "email-error" : undefined}
                  data-testid="sign-in-email-input"
                  className={cn(
                    "w-full h-11 rounded-md border bg-white px-3.5 text-[15px] outline-none transition-colors",
                    "border-border-strong focus:border-tralenex-600 focus:ring-2 focus:ring-tralenex-200",
                    emailError && "border-risk-high focus:border-risk-high focus:ring-risk-high/20",
                  )}
                  placeholder="name@company.com"
                />
                {emailError && (
                  <p
                    id="email-error"
                    className="mt-2 text-meta text-risk-high"
                    data-testid="sign-in-email-error"
                  >
                    {emailError}
                  </p>
                )}

                <label
                  htmlFor="password"
                  className="block text-meta font-semibold text-ink-primary mb-2 mt-4"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={!!passwordError}
                  aria-describedby={passwordError ? "password-error" : undefined}
                  data-testid="sign-in-password-input"
                  className={cn(
                    "w-full h-11 rounded-md border bg-white px-3.5 text-[15px] outline-none transition-colors",
                    "border-border-strong focus:border-tralenex-600 focus:ring-2 focus:ring-tralenex-200",
                    passwordError && "border-risk-high focus:border-risk-high focus:ring-risk-high/20",
                  )}
                  placeholder="Your password"
                />
                {passwordError && (
                  <p
                    id="password-error"
                    className="mt-2 text-meta text-risk-high"
                    data-testid="sign-in-password-error"
                  >
                    {passwordError}
                  </p>
                )}

                {serverError && (
                  <p
                    className="mt-3 text-meta text-risk-high"
                    data-testid="sign-in-server-error"
                  >
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="sign-in-submit-button"
                  className={cn(
                    "mt-4 w-full h-11 inline-flex items-center justify-center gap-2",
                    "rounded-md bg-tralenex-600 text-white text-[15px] font-semibold",
                    "hover:bg-tralenex-700 active:bg-tralenex-900 transition-colors",
                    submitting && "opacity-70 cursor-not-allowed",
                  )}
                >
                  {submitting ? "Signing in…" : "Sign in"}
                  {!submitting && <ArrowRight size={16} />}
                </button>
              </form>
            </>
          )}
        </div>
      </main>

      {/* Footer — single line, no clutter */}
      <footer className="px-10 lg:px-14 pb-6 text-meta text-ink-secondary">
        <span data-testid="sign-in-footer">
          ALGOS by TRALENEX
        </span>
      </footer>
    </div>
  );
}

function PersonaStep({ email, onSelect, onBack, personas, tenantName }) {
  return (
    <div data-testid="sign-in-persona-step">
      <p className="eyebrow text-ink-secondary mb-3">Continue as</p>
      <h2 className="text-h1 text-ink-primary mb-1">Select a persona</h2>
      <p className="text-meta text-ink-secondary mb-6">
        Signed in as <span className="text-ink-primary font-medium">{email}</span>.
        {tenantName ? (
          <>
            {" "}You are previewing <span className="text-ink-primary font-medium">{tenantName}</span> — pick any executive&apos;s vantage point.
          </>
        ) : (
          <> Demo Mode lets you preview ALGOS from any executive&apos;s vantage point.</>
        )}
      </p>

      <div className="grid grid-cols-1 gap-1.5 max-h-[420px] overflow-y-auto pr-1">
        {personas.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            data-testid={`sign-in-persona-${p.id}`}
            className="group flex items-center gap-3 rounded-md border border-border-subtle bg-white px-3 py-2.5 text-left transition-colors hover:border-tralenex-300 hover:bg-tralenex-50/40 focus-visible:border-tralenex-600"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-inset text-[12px] font-semibold text-ink-primary shrink-0 group-hover:bg-tralenex-600 group-hover:text-white transition-colors">
              {p.initials}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-ink-primary truncate">
                {p.name}
              </span>
              <span className="block text-meta text-ink-secondary truncate">
                {p.role}
              </span>
            </span>
            <span className="text-meta font-medium uppercase tracking-[0.08em] text-ink-disabled shrink-0">
              {p.roleCode}
            </span>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onBack}
        className="mt-5 text-meta text-ink-secondary hover:text-ink-primary transition-colors"
        data-testid="sign-in-back-button"
      >
        ← Use a different email
      </button>
    </div>
  );
}
